import React from "react";
import { createPortal } from "react-dom";
import { activateDialog } from "./dialogFocus";
/**
 * A question over the page, for actions that cannot be undone from where the volunteer is standing.
 * Plain React rather than window.confirm: the browser's own dialog looks like nothing else on the
 * site on a phone, and cannot be styled to make the safe answer the obvious one.
 *
 * "Nej" is focused, and Escape and a tap on the backdrop both mean no - backing out is always the
 * easy thing to do.
 *
 * An optional third answer sits between yes and no. With three, the buttons stack full width
 * instead of sitting in a row: three labels of any length do not fit side by side on a phone.
 *
 * The yes button is red by default, since most of these ask before something is taken away. Pass
 * `positive` when yes is a positive answer - accepting a swap - and it takes the green of the button
 * that opened the dialog instead. Not being undoable is no reason for red on its own: a swap cannot
 * be undone either, and red would read as a warning against doing it.
 */
export default class ConfirmDialog extends React.Component {
    constructor() {
        super(...arguments);
        this.dialogRef = React.createRef();
        // Whoever asked has the focus until this is drawn, and "Nej" takes it then (autoFocus).
        this.opener = document.activeElement;
    }
    componentDidMount() {
        // Escape means no, Tab stays in the dialog, and the focus goes back to the button that asked.
        this.deactivate = activateDialog(() => this.dialogRef.current, () => this.props.onNo(), this.opener);
    }
    componentWillUnmount() {
        var _a;
        (_a = this.deactivate) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    render() {
        var _a, _b;
        const { extra, busy } = this.props;
        // Into the body, not into the place of whoever asked: a parent with a transform or a filter
        // becomes the frame of a fixed element, and the dialog would then be centred over that parent
        // instead of over the window.
        return createPortal(React.createElement("div", { className: "bp-dialog-backdrop", onClick: this.props.onNo },
            React.createElement("div", { className: "bp-dialog", ref: this.dialogRef, tabIndex: -1, role: "alertdialog", "aria-modal": "true", "aria-label": this.props.heading ? `${this.props.heading}. ${this.props.message}` : this.props.message, onClick: (e) => e.stopPropagation() },
                this.props.heading ? React.createElement("h2", { className: "bp-dialog-heading" }, this.props.heading) : null,
                this.props.title ? React.createElement("div", { className: "bp-dialog-title" }, this.props.title) : null,
                this.props.subtitle ? React.createElement("div", { className: "bp-dialog-subtitle" }, this.props.subtitle) : null,
                this.props.title ? React.createElement("div", { className: "bp-dialog-ask" }, this.props.message) : React.createElement("p", null, this.props.message),
                React.createElement("div", { className: `bp-dialog-actions${extra ? " stacked" : ""}` },
                    React.createElement("button", { type: "button", className: "btn btn-outline-secondary", autoFocus: true, disabled: busy, onClick: this.props.onNo }, (_a = this.props.noLabel) !== null && _a !== void 0 ? _a : "Nej"),
                    extra
                        ? React.createElement("button", { type: "button", className: "btn btn-outline-danger", disabled: busy, onClick: extra.onClick }, extra.label)
                        : null,
                    React.createElement("button", { type: "button", className: `btn ${this.props.positive ? "btn-success" : "btn-danger"}`, disabled: busy, onClick: this.props.onYes }, (_b = this.props.yesLabel) !== null && _b !== void 0 ? _b : "Ja")))), document.body);
    }
}
